import type { AnyError } from "@pdfking/errors"
import type { PageNumber, PdfEngine, SinglePagePdf, VirtualFile } from "@pdfking/pdfengine"
import { FileContentGUID } from "@pdfking/pdfengine"
import { Init } from "@pdfking/reactivity/preact"
import { CacheFunction } from "@pdfking/storages"
import { Collect, Join, RecordMerge, SafeIterable, SafePromise } from "@pdfking/tfunc"

/**
 * The per-file reactive state: the source `file` plus everything the single
 * idempotent `process` op accumulates. `page_count` starts undefined (unknown
 * until the first split page arrives); `pages` fills progressively as the split
 * streams. Reactive writes replace whole records (signal granularity is the
 * top-level key).
 *
 * `process` splits once and rasterizes nothing. It resolves with `page_count`
 * as soon as the first page reveals it (0 on failure), leaving the split to
 * finish in the background. Failures are captured onto `store.error` in place,
 * so the promise is always safe. Idempotency comes from `CacheFunction` keyed
 * by content guid.
 *
 * `request` is what rasterizes, one page at a time, for whoever is about to
 * show it, and answers with the raster. It always hands the split's single
 * page to the engine — never the whole file (that would re-split internally).
 */
export const CreateFileStore = (
  engine: PdfEngine,
  file: VirtualFile,
  rasterize: (page: SinglePagePdf) => Promise<string>
) => {
  // One raster per page, however many ask: an n-up sheet mounts the same page
  // once per copy, all in the same tick.
  const rasters = new Map<PageNumber, Promise<string | undefined>>()
  // Pages asked for before the split reached them; `consume` hands them over,
  // and `settle` answers whatever is left once the split is over — so no
  // request is ever left waiting.
  const awaited = new Map<PageNumber, (page: SinglePagePdf | undefined) => void>()
  let settled = false

  const split_page = (page_number: PageNumber): Promise<SinglePagePdf | undefined> => {
    const page = store.pages[page_number]
    return page || settled
      ? Promise.resolve(page)
      : new Promise(resolve => awaited.set(page_number, resolve))
  }

  // The split is over, finished or failed: a page still awaited is not coming.
  const settle = () => {
    settled = true
    for (const resolve of awaited.values()) resolve(undefined)
    awaited.clear()
  }

  const rasterize_page = (page: SinglePagePdf | undefined): Promise<string | undefined> =>
    page ? rasterize(page) : Promise.resolve(undefined)

  const consume = async (stream: AsyncGenerator<SinglePagePdf>): Promise<void> => {
    for await (const page of stream) {
      store.pages = RecordMerge(store.pages, { [page.page_number]: page })
      awaited.get(page.page_number)?.(page)
      awaited.delete(page.page_number)
    }
    settle()
  }

  const process = CacheFunction(async (f: VirtualFile): Promise<PageNumber> => {
    const { value: safe } = SafeIterable(engine.SplitPdf([f]), error => {
      if (error) store.error = error
    })
    const head = await Collect(safe, 1)
    const [first] = head
    if (!first) {
      settle()
      return 0 as PageNumber
    }
    store.page_count = first.page_count
    // Stream the pages in the background; return the count now.
    void consume(Join(head, safe))
    return first.page_count
  }, FileContentGUID)

  const store = Init({
    data: {
      error: undefined as AnyError | undefined,
      file,
      page_count: undefined as PageNumber | undefined,
      pages: {} as Record<PageNumber, SinglePagePdf>,
    },
    methods: {
      // Split once; resolves with the page count as soon as it is known (0 on
      // failure — see `error`), the rest streaming in the background.
      // Idempotent, and already a safe promise.
      process(): Promise<PageNumber> {
        return process(this.file)
      },
      // The raster url of one page — made once, now if the split already reached
      // the page, as soon as it does otherwise. Idempotent: the same page, the
      // same promise. Already a safe promise: a failure lands on `error` and
      // answers undefined.
      request(page_number: PageNumber): Promise<string | undefined> {
        const existing = rasters.get(page_number)
        if (existing) return existing
        const raster = SafePromise(split_page(page_number).then(rasterize_page)).then(
          ({ error, value }) => {
            if (error) store.error = error
            return value
          }
        )
        rasters.set(page_number, raster)
        return raster
      },
    },
  })

  return store
}

export type FileStore = ReturnType<typeof CreateFileStore>
